import { memo, useEffect, useId, useRef } from "react";
import {
  initFlowbite,
  SunIcon,
  MoonIcon,
  CheckIcon,
  AdjustmentsVerticalIcon,
} from "@/index.js";
import { useThemeStore } from "@/shared/stores/useThemeStore";

export const THEME_OPTIONS = [
  { value: "system", label: "حسب النظام", Icon: AdjustmentsVerticalIcon },
  { value: "light", label: "فاتح", Icon: SunIcon },
  { value: "dark", label: "داكن", Icon: MoonIcon },
];

const ThemeSwitcher = ({ className = "" }) => {
  const { initTheme, setTheme, theme } = useThemeStore();
  const buttonRef = useRef(null);
  const dropdownId = `dropdown-theme-${useId().replace(/:/g, "")}`;

  const ActiveThemeIcon =
    (THEME_OPTIONS.find((o) => o.value === theme) ?? THEME_OPTIONS[0]).Icon;

  useEffect(() => {
    initTheme();
    const t = setTimeout(() => initFlowbite(), 0);
    return () => clearTimeout(t);
  }, [initTheme]);

  return (
    <div className={`flex items-center ${className}`}>
      <button
        ref={buttonRef}
        type="button"
        aria-label="مظهر الواجهة"
        title="مظهر الواجهة"
        data-dropdown-toggle={dropdownId}
        data-dropdown-placement="bottom"
        className="group relative flex h-10 w-10 items-center justify-center rounded-full bg-white text-gray-500 ring-1 ring-gray-200 transition-all duration-200 hover:bg-purple-50 hover:text-purple-900 hover:ring-purple-200 active:scale-95 dark:bg-gray-900 dark:text-gray-300 dark:ring-gray-700 dark:hover:bg-gray-900/50 dark:hover:text-purple-300 dark:hover:ring-purple-700/40"
      >
        <ActiveThemeIcon className="h-5 w-5" />
      </button>

      <div
        id={dropdownId}
        className="z-[60] my-3 hidden w-52 list-none overflow-hidden rounded-2xl border border-gray-100 bg-white/95 text-base shadow-xl shadow-gray-900/10 backdrop-blur-xl dark:border-gray-700/60 dark:bg-gray-900/95 dark:text-gray-100 dark:shadow-black/40"
      >
        <div className="border-b border-gray-100 px-4 py-2.5 dark:border-gray-800">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-purple-700 dark:text-purple-400">
            المظهر
          </p>
        </div>

        <ul className="space-y-1 p-2" role="none">
          {THEME_OPTIONS.map(({ value, label, Icon }) => {
            const active = theme === value;
            return (
              <li key={value}>
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => {
                    setTheme(value);
                    // Close the Flowbite dropdown after choosing.
                    buttonRef.current?.click();
                  }}
                  className={`group/item flex w-full items-center gap-3 rounded-2xl px-3 py-2 text-sm font-medium transition-colors ${
                    active
                      ? "bg-purple-50 text-purple-900 dark:bg-purple-950/30 dark:text-purple-300"
                      : "text-gray-700 hover:bg-gray-50 hover:text-purple-900 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-purple-300"
                  }`}
                >
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 transition-colors ${
                      active
                        ? "bg-white text-purple-700 ring-purple-100 dark:bg-gray-900 dark:text-purple-300 dark:ring-purple-800/50"
                        : "bg-gray-50 text-gray-500 ring-gray-100 dark:bg-gray-800 dark:text-gray-400 dark:ring-gray-700"
                    }`}
                  >
                    <Icon className="h-[18px] w-[18px]" />
                  </span>
                  <span className="flex-1 text-start tracking-tight">
                    {label}
                  </span>
                  {active && (
                    <CheckIcon className="h-4 w-4 shrink-0 text-purple-700 dark:text-purple-400" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};

export default memo(ThemeSwitcher);
